import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import {
  Avatar,
  Badge,
  Box,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Divider,
  Typography,
  Tooltip,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import TimerOutlinedIcon from '@mui/icons-material/TimerOutlined';
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';
import BarChartOutlinedIcon from '@mui/icons-material/BarChartOutlined';
import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined';
import ViewWeekOutlinedIcon from '@mui/icons-material/ViewWeekOutlined';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import NotificationsOutlinedIcon from '@mui/icons-material/NotificationsOutlined';
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../core/auth/useAuth';
import { colors } from '../core/theme/theme';
import { useUnreadCount } from '../features/notifications/useNotifications';

const SIDEBAR_WIDTH = 256;
const SIDEBAR_WIDTH_COLLAPSED = 72;

export { SIDEBAR_WIDTH, SIDEBAR_WIDTH_COLLAPSED };

type SidebarProps = {
  collapsed?: boolean;
  onToggle?: () => void;
  onOpenPalette?: () => void;
};

export default function Sidebar({ collapsed = false, onToggle, onOpenPalette }: SidebarProps) {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [kronosOpen, setKronosOpen] = useState(location.pathname.startsWith('/kronos'));
  const [analyseOpen, setAnalyseOpen] = useState(
    location.pathname.startsWith('/kronos/dashboard') ||
    location.pathname.startsWith('/kronos/report'),
  );
  const unreadCount = useUnreadCount(user?.uid);

  const isAdmin = user?.roles.includes('admin') ?? false;
  const canSeeProjectLife = isAdmin || (user?.roles.includes('direction') ?? false);

  const initials = user?.displayName
    ?.split(' ')
    .map((n) => n[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

  const isActive = (path: string) =>
    location.pathname === path || location.pathname.startsWith(path + '/');

  const activeStyle = (path: string) =>
    isActive(path)
      ? {
          borderLeft: `3px solid ${colors.yellow}`,
          bgcolor: 'rgba(255, 250, 94, 0.10)',
        }
      : {
          borderLeft: '3px solid transparent',
        };

  const handleKronosClick = () => {
    if (collapsed) {
      navigate('/kronos/tracker');
    } else {
      setKronosOpen(!kronosOpen);
    }
  };

  // When collapsed, wrap buttons in a tooltip so labels remain discoverable on hover.
  const withTooltip = (label: string, node: React.ReactElement) =>
    collapsed ? (
      <Tooltip title={label} placement="right" arrow>
        {node}
      </Tooltip>
    ) : (
      node
    );

  const iconSlotSx = {
    minWidth: collapsed ? 0 : 40,
    justifyContent: 'center',
    mr: collapsed ? 0 : undefined,
  };

  const width = collapsed ? SIDEBAR_WIDTH_COLLAPSED : SIDEBAR_WIDTH;

  return (
    <Box
      component="nav"
      sx={{
        width,
        flexShrink: 0,
        bgcolor: colors.darkBg,
        height: '100vh',
        position: 'fixed',
        top: 0,
        left: 0,
        display: 'flex',
        flexDirection: 'column',
        overflowX: 'hidden',
        overflowY: 'auto',
        transition: 'width 0.2s ease',
        zIndex: (theme) => theme.zIndex.appBar + 1,
      }}
    >
      {/* Logo + hamburger toggle */}
      <Box
        sx={{
          px: collapsed ? 0 : 1.5,
          py: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: collapsed ? 'center' : 'space-between',
          gap: 0.5,
          minHeight: 64,
        }}
      >
        {!collapsed && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
            <Box
              component="img"
              src="/images/altyor-logo.svg"
              alt="Altyor"
              sx={{ height: 24, filter: 'brightness(0) invert(1)' }}
            />
            <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontWeight: 700, fontSize: 20 }}>
              Hub
            </Typography>
          </Box>
        )}
        <Tooltip title={t('nav.toggleMenu')} placement="right" arrow>
          <IconButton
            onClick={onToggle}
            aria-label={t('nav.toggleMenu')}
            size="small"
            sx={{ color: 'rgba(255,255,255,0.7)' }}
          >
            <MenuIcon />
          </IconButton>
        </Tooltip>
      </Box>

      <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)' }} />

      <List component="nav" sx={{ flex: 1, py: 1 }}>
        {/* Projects Hub */}
        {withTooltip(
          t('nav.projectsHub'),
          <ListItemButton
            onClick={() => navigate('/projects')}
            sx={{
              ...activeStyle('/projects'),
              '&:hover': { bgcolor: 'rgba(255,255,255,0.06)' },
              py: 1.5,
              justifyContent: collapsed ? 'center' : 'flex-start',
            }}
          >
            <ListItemIcon sx={iconSlotSx}>
              <FolderOutlinedIcon
                sx={{ color: isActive('/projects') ? '#fff' : 'rgba(255,255,255,0.5)' }}
              />
            </ListItemIcon>
            {!collapsed && (
              <ListItemText
                primary={t('nav.projectsHub')}
                slotProps={{
                  primary: {
                    sx: {
                      fontWeight: isActive('/projects') ? 600 : 400,
                      color: '#fff',
                    },
                  },
                }}
              />
            )}
          </ListItemButton>,
        )}

        {/* Vie des projets — conformité des jalons, tous projets confondus.
            Réservé à admin/direction : c'est l'audience de l'écran 4 (la qualité
            et la direction). Un chef de projet a la même information sur l'onglet
            Jalons de ses projets et dans le rappel quotidien, qui lui est déjà
            restreint côté serveur. */}
        {canSeeProjectLife &&
          withTooltip(
            t('nav.projectLife'),
            <ListItemButton
              onClick={() => navigate('/project-life')}
              sx={{
                ...activeStyle('/project-life'),
                '&:hover': { bgcolor: 'rgba(255,255,255,0.06)' },
                py: 1.5,
                justifyContent: collapsed ? 'center' : 'flex-start',
              }}
            >
              <ListItemIcon sx={iconSlotSx}>
                <FactCheckOutlinedIcon
                  sx={{ color: isActive('/project-life') ? '#fff' : 'rgba(255,255,255,0.5)' }}
                />
              </ListItemIcon>
              {!collapsed && (
                <ListItemText
                  primary={t('nav.projectLife')}
                  slotProps={{
                    primary: {
                      sx: {
                        fontWeight: isActive('/project-life') ? 600 : 400,
                        color: '#fff',
                      },
                    },
                  }}
                />
              )}
            </ListItemButton>,
          )}

        {/* Kronos (expandable when not collapsed) */}
        {withTooltip(
          t('nav.kronos'),
          <ListItemButton
            onClick={handleKronosClick}
            sx={{
              ...activeStyle('/kronos'),
              '&:hover': { bgcolor: 'rgba(255,255,255,0.06)' },
              py: 1.5,
              justifyContent: collapsed ? 'center' : 'flex-start',
            }}
          >
            <ListItemIcon sx={iconSlotSx}>
              <AccessTimeOutlinedIcon
                sx={{ color: isActive('/kronos') ? '#fff' : 'rgba(255,255,255,0.5)' }}
              />
            </ListItemIcon>
            {!collapsed && (
              <>
                <ListItemText
                  primary={t('nav.kronos')}
                  slotProps={{
                    primary: {
                      sx: {
                        fontWeight: isActive('/kronos') ? 600 : 400,
                        color: '#fff',
                      },
                    },
                  }}
                />
                {kronosOpen ? <ExpandLess sx={{ color: 'rgba(255,255,255,0.5)' }} /> : <ExpandMore sx={{ color: 'rgba(255,255,255,0.5)' }} />}
              </>
            )}
          </ListItemButton>,
        )}

        {!collapsed && (
          <Collapse in={kronosOpen} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {/* Tracker */}
              <ListItemButton
                onClick={() => navigate('/kronos/tracker')}
                sx={{
                  pl: 6,
                  ...activeStyle('/kronos/tracker'),
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.06)' },
                }}
              >
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <TimerOutlinedIcon fontSize="small" sx={{ color: 'rgba(255,255,255,0.5)' }} />
                </ListItemIcon>
                <ListItemText
                  primary={t('nav.tracker')}
                  slotProps={{ primary: { sx: { fontSize: '14px', color: 'rgba(255,255,255,0.85)' } } }}
                />
              </ListItemButton>

              {/* Timesheet */}
              <ListItemButton
                onClick={() => navigate('/kronos/timesheet')}
                sx={{
                  pl: 6,
                  ...activeStyle('/kronos/timesheet'),
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.06)' },
                }}
              >
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <ViewWeekOutlinedIcon fontSize="small" sx={{ color: 'rgba(255,255,255,0.5)' }} />
                </ListItemIcon>
                <ListItemText
                  primary={t('nav.timesheet')}
                  slotProps={{ primary: { sx: { fontSize: '14px', color: 'rgba(255,255,255,0.85)' } } }}
                />
              </ListItemButton>

              {/* Calendar */}
              <ListItemButton
                onClick={() => navigate('/kronos/calendar')}
                sx={{
                  pl: 6,
                  ...activeStyle('/kronos/calendar'),
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.06)' },
                }}
              >
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <CalendarMonthIcon fontSize="small" sx={{ color: 'rgba(255,255,255,0.5)' }} />
                </ListItemIcon>
                <ListItemText
                  primary={t('nav.calendar')}
                  slotProps={{ primary: { sx: { fontSize: '14px', color: 'rgba(255,255,255,0.85)' } } }}
                />
              </ListItemButton>

              {/* Analyse — everyone; the server scopes employees to their own entries,
                  and the Excel export button is gated inside KronosReport */}
                  <ListItemButton
                    onClick={() => setAnalyseOpen(!analyseOpen)}
                    sx={{
                      pl: 6,
                      borderLeft: (isActive('/kronos/dashboard') || isActive('/kronos/report'))
                        ? `3px solid ${colors.yellow}`
                        : '3px solid transparent',
                      bgcolor: (isActive('/kronos/dashboard') || isActive('/kronos/report'))
                        ? 'rgba(255, 250, 94, 0.10)'
                        : undefined,
                      '&:hover': { bgcolor: 'rgba(255,255,255,0.06)' },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <BarChartOutlinedIcon fontSize="small" sx={{ color: 'rgba(255,255,255,0.5)' }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={t('nav.analyse')}
                      slotProps={{ primary: { sx: { fontSize: '14px', color: 'rgba(255,255,255,0.85)' } } }}
                    />
                    {analyseOpen ? <ExpandLess fontSize="small" sx={{ color: 'rgba(255,255,255,0.5)' }} /> : <ExpandMore fontSize="small" sx={{ color: 'rgba(255,255,255,0.5)' }} />}
                  </ListItemButton>

                  <Collapse in={analyseOpen} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding>
                      <ListItemButton
                        onClick={() => navigate('/kronos/dashboard')}
                        sx={{
                          pl: 9,
                          ...activeStyle('/kronos/dashboard'),
                          '&:hover': { bgcolor: 'rgba(255,255,255,0.06)' },
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 28 }}>
                          <DashboardOutlinedIcon
                            sx={{ fontSize: 16, color: 'rgba(255,255,255,0.5)' }}
                          />
                        </ListItemIcon>
                        <ListItemText
                          primary={t('nav.dashboard')}
                          slotProps={{ primary: { sx: { fontSize: '13px', color: 'rgba(255,255,255,0.85)' } } }}
                        />
                      </ListItemButton>

                      <ListItemButton
                        onClick={() => navigate('/kronos/report')}
                        sx={{
                          pl: 9,
                          ...activeStyle('/kronos/report'),
                          '&:hover': { bgcolor: 'rgba(255,255,255,0.06)' },
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 28 }}>
                          <AssessmentOutlinedIcon
                            sx={{ fontSize: 16, color: 'rgba(255,255,255,0.5)' }}
                          />
                        </ListItemIcon>
                        <ListItemText
                          primary={t('nav.report')}
                          slotProps={{ primary: { sx: { fontSize: '13px', color: 'rgba(255,255,255,0.85)' } } }}
                        />
                      </ListItemButton>
                    </List>
                  </Collapse>
            </List>
          </Collapse>
        )}

        {/* Clients — readable by everyone (admin-only write is enforced in the UI + Firestore rules) */}
        {withTooltip(
          t('nav.customers'),
          <ListItemButton
            onClick={() => navigate('/customers')}
            sx={{
              ...activeStyle('/customers'),
              '&:hover': { bgcolor: 'rgba(255,255,255,0.06)' },
              py: 1.5,
              justifyContent: collapsed ? 'center' : 'flex-start',
            }}
          >
            <ListItemIcon sx={iconSlotSx}>
              <BusinessOutlinedIcon
                sx={{ color: isActive('/customers') ? '#fff' : 'rgba(255,255,255,0.5)' }}
              />
            </ListItemIcon>
            {!collapsed && (
              <ListItemText
                primary={t('nav.customers')}
                slotProps={{
                  primary: {
                    sx: {
                      fontWeight: isActive('/customers') ? 600 : 400,
                      color: '#fff',
                    },
                  },
                }}
              />
            )}
          </ListItemButton>,
        )}

        {/* Settings — admin only */}
        {isAdmin &&
          withTooltip(
            t('nav.settings'),
            <ListItemButton
              onClick={() => navigate('/settings')}
              sx={{
                ...activeStyle('/settings'),
                '&:hover': { bgcolor: 'rgba(255,255,255,0.06)' },
                py: 1.5,
                justifyContent: collapsed ? 'center' : 'flex-start',
              }}
            >
              <ListItemIcon sx={iconSlotSx}>
                <SettingsOutlinedIcon
                  sx={{ color: isActive('/settings') ? '#fff' : 'rgba(255,255,255,0.5)' }}
                />
              </ListItemIcon>
              {!collapsed && (
                <ListItemText
                  primary={t('nav.settings')}
                  slotProps={{
                    primary: {
                      sx: {
                        fontWeight: isActive('/settings') ? 600 : 400,
                        color: '#fff',
                      },
                    },
                  }}
                />
              )}
            </ListItemButton>,
          )}
      </List>

      {/* Alfred — shortcut hint */}
      <Box
        onClick={onOpenPalette}
        sx={{
          mx: collapsed ? 1 : 1.5,
          mb: 1,
          px: collapsed ? 0 : 1.5,
          py: collapsed ? 0.75 : 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: collapsed ? 'center' : 'flex-start',
          gap: 1,
          borderRadius: '6px',
          cursor: 'pointer',
          transition: 'background-color 0.15s',
          '&:hover': { bgcolor: 'rgba(255,255,255,0.06)' },
        }}
      >
        {collapsed ? (
          <Tooltip title={t('search.alfredHint')} placement="right" arrow>
            <Typography sx={{ fontSize: 16, lineHeight: 1, userSelect: 'none' }}>
              🥷
            </Typography>
          </Tooltip>
        ) : (
          <>
            <Typography sx={{ fontSize: 14, lineHeight: 1, userSelect: 'none' }}>
              🥷
            </Typography>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                variant="caption"
                sx={{
                  color: 'rgba(255,255,255,0.4)',
                  fontSize: 11,
                  fontWeight: 500,
                  display: 'block',
                  lineHeight: 1.3,
                  userSelect: 'none',
                }}
              >
                {t('search.alfredHint')}
              </Typography>
            </Box>
            <Typography
              component="kbd"
              sx={{
                color: 'rgba(255,255,255,0.4)',
                fontSize: 11,
                fontWeight: 600,
                fontFamily: 'inherit',
                bgcolor: 'rgba(255,255,255,0.08)',
                px: 0.75,
                py: 0.15,
                borderRadius: '4px',
                lineHeight: 1.6,
                userSelect: 'none',
                flexShrink: 0,
              }}
            >
              ⌘K
            </Typography>
          </>
        )}
      </Box>

      {/* User profile + sign out — pinned to bottom */}
      <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)' }} />
      <Box
        sx={{
          p: collapsed ? 1 : 1.5,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          justifyContent: collapsed ? 'center' : 'flex-start',
        }}
      >
        <Avatar
          src={user?.photoURL ?? undefined}
          sx={{
            width: 34,
            height: 34,
            borderRadius: 0,
            bgcolor: 'rgba(255,255,255,0.15)',
            color: '#fff',
            fontSize: '12px',
            fontWeight: 600,
            flexShrink: 0,
          }}
        >
          {initials}
        </Avatar>
        {!collapsed && (
          <>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography variant="body2" noWrap sx={{ color: '#fff', fontWeight: 500, lineHeight: 1.3 }}>
                {user?.displayName}
              </Typography>
              <Typography variant="caption" noWrap sx={{ color: 'rgba(255,255,255,0.45)', lineHeight: 1.3 }}>
                {user?.jobTitle ?? user?.email}
              </Typography>
            </Box>
            <IconButton
              onClick={() => navigate('/notifications')}
              size="small"
              aria-label={t('nav.notifications')}
              sx={{ flexShrink: 0 }}
            >
              <Badge badgeContent={unreadCount} color="secondary">
                <NotificationsOutlinedIcon
                  fontSize="small"
                  sx={{ color: unreadCount > 0 ? colors.red : 'rgba(255,255,255,0.5)' }}
                />
              </Badge>
            </IconButton>
            <Tooltip title={t('auth.logout')} placement="top" arrow>
              <IconButton
                onClick={logout}
                size="small"
                aria-label={t('auth.logout')}
                sx={{ color: 'rgba(255,255,255,0.5)', '&:hover': { color: colors.red } }}
              >
                <LogoutOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </>
        )}
      </Box>
    </Box>
  );
}
